import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, MessageCircle, CheckCircle2 } from "lucide-react";
import { ScrollReveal, SectionLabel, EASE } from "./Reveal";
import { LINKS } from "@/constants/links";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-[#0A0A0A] border border-white/15 px-5 py-4 text-[#F2F0EB] placeholder:text-[#55554F] transition-colors duration-300";

const projectTypes = ["Wedding", "Filming + Social + Ads (everything handled)", "Brand Story", "Social Content", "Event Film", "Monthly Package", "Personal Story", "Not sure yet"];

export const ContactSection = () => {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", project_type: "", message: "", budget: "", team: "" });

  useEffect(() => {
    const onPrefill = (e) => {
      setOpen(true);
      setForm((f) => ({ ...f, project_type: e.detail?.project_type || f.project_type }));
    };
    window.addEventListener("etia:prefill", onPrefill);
    return () => window.removeEventListener("etia:prefill", onPrefill);
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, {
        name: form.name,
        email: form.email,
        project_type: form.project_type,
        message: form.message,
        budget: form.budget || null,
        package_interest: form.team || null,
      });
      setDone(true);
      toast.success("Enquiry sent — Jacques usually replies within 24 hours.");
    } catch (e) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 lg:py-32">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <ScrollReveal>
          <SectionLabel index="10" title="Contact" />
          <h2 className="font-display leading-[0.95] text-[11vw] sm:text-[8vw] lg:text-[6vw]">
            Let's create <span className="text-outline">something.</span>
          </h2>
          <p className="mt-8 text-base md:text-lg text-[#8A8A85] max-w-xl">
            Tap below to open the enquiry form. I usually reply within 24 hours.
          </p>
        </ScrollReveal>

        <ScrollReveal delay={0.1}>
          <div className="mt-12 flex flex-wrap gap-5">
            <button
              data-testid="contact-send-enquiry-btn"
              onClick={() => setOpen(!open)}
              className="group flex items-center gap-3 bg-[#F2F0EB] text-[#0A0A0A] font-display text-sm tracking-[0.08em] px-10 py-5 rounded-full hover:scale-105 transition-transform duration-300"
            >
              Send Enquiry
              <ArrowRight className={`w-4 h-4 transition-transform duration-300 ${open ? "rotate-90" : "group-hover:translate-x-1.5"}`} />
            </button>
            <a
              data-testid="contact-whatsapp-btn"
              href={LINKS.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-3 border border-white/20 font-display text-sm tracking-[0.08em] px-10 py-5 rounded-full hover:bg-[#F2F0EB] hover:text-[#0A0A0A] hover:border-[#F2F0EB] transition-colors duration-300"
            >
              <MessageCircle className="w-4 h-4" />
              Message on WhatsApp
            </a>
          </div>
        </ScrollReveal>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.6, ease: EASE }}
              className="overflow-hidden"
            >
              <div className="mt-10 max-w-3xl border border-white/10 bg-[#111111] p-8 lg:p-12" data-testid="enquiry-form">
                {done ? (
                  <div className="text-center py-10" data-testid="enquiry-success">
                    <CheckCircle2 className="w-12 h-12 text-[#F2F0EB] mx-auto" />
                    <h3 className="font-display text-2xl mt-6">Enquiry sent.</h3>
                    <p className="text-[#8A8A85] mt-3">Thanks, {form.name.split(" ")[0]}. I'll get back to you within 24 hours.</p>
                    <button
                      data-testid="enquiry-new-btn"
                      onClick={() => { setDone(false); setForm({ name: "", email: "", project_type: "", message: "", budget: "", team: "" }); }}
                      className="mt-8 font-mono2 text-xs uppercase tracking-[0.25em] text-[#8A8A85] hover:text-[#F2F0EB] transition-colors"
                    >
                      Send another enquiry
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <input data-testid="enquiry-name-input" className={inputCls} placeholder="Your name" value={form.name} onChange={(e) => set("name", e.target.value)} />
                      <input data-testid="enquiry-email-input" type="email" className={inputCls} placeholder="Email address" value={form.email} onChange={(e) => set("email", e.target.value)} />
                    </div>
                    <select
                      data-testid="enquiry-type-select"
                      className={`${inputCls} appearance-none ${form.project_type ? "" : "text-[#55554F]"}`}
                      value={form.project_type}
                      onChange={(e) => set("project_type", e.target.value)}
                    >
                      <option value="" disabled>What do you need?</option>
                      {projectTypes.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                    {form.project_type === "Wedding" && (
                      <div className="grid sm:grid-cols-2 gap-4" data-testid="wedding-fields">
                        <select
                          data-testid="wedding-budget-select"
                          className={`${inputCls} appearance-none ${form.budget ? "" : "text-[#55554F]"}`}
                          value={form.budget}
                          onChange={(e) => set("budget", e.target.value)}
                        >
                          <option value="" disabled>What budget are you working with?</option>
                          {["Under £1,000", "£1,000 – £2,000", "£2,000 – £3,500", "£3,500+", "Not sure yet"].map((b) => (
                            <option key={b} value={b}>{b}</option>
                          ))}
                        </select>
                        <select
                          data-testid="wedding-team-select"
                          className={`${inputCls} appearance-none ${form.team ? "" : "text-[#55554F]"}`}
                          value={form.team}
                          onChange={(e) => set("team", e.target.value)}
                        >
                          <option value="" disabled>Solo filmmaker or full team?</option>
                          {["Just a filmmaker", "Full team", "Not sure — advise me"].map((t) => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    <textarea
                      data-testid="enquiry-message-input"
                      rows={5}
                      className={`${inputCls} resize-none`}
                      placeholder="Tell me about your project — goals, timeline, links…"
                      value={form.message}
                      onChange={(e) => set("message", e.target.value)}
                    />
                    <button
                      data-testid="enquiry-submit-btn"
                      disabled={submitting || !form.name || !form.email || !form.project_type}
                      onClick={submit}
                      className="group flex items-center gap-3 bg-[#F2F0EB] text-[#0A0A0A] font-display text-sm tracking-[0.08em] px-10 py-4 rounded-full disabled:opacity-30 disabled:cursor-not-allowed hover:scale-105 transition-all duration-300"
                    >
                      {submitting ? "Sending…" : "Send Enquiry"}
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1.5 transition-transform duration-300" />
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};
